import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { PROJECT_TYPES, PRIORITIES, PROJECT_STATUSES } from "./projectUtils";
import AttachmentPicker from "./AttachmentPicker";
import { hasDev } from "../../lib/services";
import CurrencySelect from "../CurrencySelect";
import HandoverDialog from "./HandoverDialog";
import { canSeeCosts } from "../../lib/services";
import { useAuth } from "../../context/AuthContext";

const empty = {
  project_name: "",
  client_id: "",
  description: "",
  project_type: "",
  project_manager_id: "",
  development_team: [],
  start_date: "",
  expected_completion_date: "",
  priority: "Medium",
  status: "Not Started",
  estimated_budget: "",
  actual_cost: "",
  currency: "USD",
  attachments: [],
  notes: "",
};

export default function ProjectForm({ open, onOpenChange, initial, clients = [], users = [], onSaved }) {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [handover, setHandover] = useState(null);
  const [handoverMap, setHandoverMap] = useState({});
  const [loads, setLoads] = useState({ here: {}, all: {} });
  const { user } = useAuth();
  const showCosts = canSeeCosts(user);

  useEffect(() => {
    if (open) {
      setErrors({});
      setForm(initial ? { ...empty, ...initial, actual_cost: initial.actual_cost || "" } : empty);
    }
  }, [open, initial]);

  const pms = users.filter(u => ["admin", "manager", "project_manager"].includes(u.role));
  const devs = users.filter(u => ["developer", "tester"].includes(u.role));

  const toggleDev = (id) => setForm(f => ({
    ...f,
    development_team: f.development_team.includes(id)
      ? f.development_team.filter(x => x !== id)
      : [...f.development_team, id]
  }));

  const checkHandover = async () => {
    const removedIds = (initial?.development_team || []).filter(id => !form.development_team.includes(id));
    if (!initial?.id || !removedIds.length) return false;
    const [{ data }, { data: all }] = await Promise.all([
      api.get(`/projects/${initial.id}/open-task-counts`).catch(() => ({ data: {} })),
      api.get(`/projects/${initial.id}/open-task-counts?scope=all`).catch(() => ({ data: {} })),
    ]);
    const removed = removedIds.filter(id => data[id] > 0).map(id => ({ id, name: users.find(u => u.id === id)?.name || "Removed member", count: data[id] }));
    if (!removed.length) return false;
    setLoads({ here: data, all });
    setHandoverMap({});
    setHandover(removed);
    return true;
  };

  const save = async (handoverPayload) => {
    const e = {};
    if (!form.project_name.trim()) e.project_name = "Project name is required.";
    if (!form.project_type) e.project_type = "Please select a project type.";
    if (!form.project_manager_id) e.project_manager_id = "Please choose a Project Manager so the project shows on their dashboard.";
    if (!form.start_date) e.start_date = "Start date is required.";
    if (!form.expected_completion_date) e.expected_completion_date = "Expected completion date is required.";
    else if (form.start_date && form.expected_completion_date < form.start_date) e.expected_completion_date = "Completion date cannot be earlier than the start date.";
    if (showCosts) {
      if (form.estimated_budget === "" || form.estimated_budget === null) e.estimated_budget = "Estimated budget is required.";
      else if (!(Number(form.estimated_budget) > 0)) e.estimated_budget = "Amount must be greater than 0.";
      if (form.actual_cost !== "" && form.actual_cost !== null && !(Number(form.actual_cost) > 0)) e.actual_cost = "Amount must be greater than 0.";
    }
    setErrors(e);
    if (Object.keys(e).length) { toast.error(Object.values(e)[0]); return; }
    if (!handoverPayload && await checkHandover()) return;
    setBusy(true);
    try {
      const payload = { ...form, estimated_budget: Number(form.estimated_budget), actual_cost: form.actual_cost === "" ? 0 : Number(form.actual_cost) };
      if (handoverPayload) payload.handover = handoverPayload;
      if (!showCosts) { delete payload.estimated_budget; delete payload.actual_cost; }
      if (initial?.id) await api.put(`/projects/${initial.id}`, payload);
      else await api.post("/projects", payload);
      toast.success(handoverPayload ? "Project updated and tasks handed over" : initial ? "Project updated" : "Project created");
      setHandover(null);
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FFFFFF] border-[#E4E7EC] max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="project-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">{initial ? "Edit Project" : "Create Project"}</DialogTitle>
          <DialogDescription className="text-neutral-600 text-xs">Set up the project scope, team, timeline, and budget.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <F label="Project Name" required span={2} error={errors.project_name}><Input value={form.project_name} onChange={e => setForm({...form, project_name: e.target.value})} data-testid="pf-name" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          <F label="Client">
            <Select value={form.client_id || "none"} onValueChange={v => {
              const prev = clients.find(c => c.id === form.client_id);
              const cl = clients.find(c => c.id === v);
              const samePm = !form.project_manager_id || form.project_manager_id === (prev?.assigned_project_manager || "");
              const prevTeam = prev?.assigned_developers || [];
              const sameTeam = !form.development_team?.length || (form.development_team.length === prevTeam.length && form.development_team.every(x => prevTeam.includes(x)));
              setForm(f => ({
                ...f,
                client_id: v === "none" ? "" : v,
                project_manager_id: samePm ? (cl?.assigned_project_manager || "") : f.project_manager_id,
                development_team: sameTeam ? (cl?.assigned_developers || []) : f.development_team,
              }));
            }}>
              <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="pf-client"><SelectValue placeholder="Select client" /></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">
                <SelectItem value="none">— None —</SelectItem>
                {clients.filter(c => hasDev(c) || !c.services?.length || c.id === form.client_id).map(c => <SelectItem key={c.id} value={c.id}>{c.client_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
          <F label="Project Type" required error={errors.project_type}>
            <Select value={form.project_type} onValueChange={v => setForm({...form, project_type: v})}>
              <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="pf-type"><SelectValue placeholder="Select project type" /></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">
                {PROJECT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
          <F label="Project Manager" required error={errors.project_manager_id}>
            <Select value={form.project_manager_id || "none"} onValueChange={v => setForm({...form, project_manager_id: v === "none" ? "" : v})}>
              <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="pf-pm"><SelectValue placeholder="Select PM" /></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">
                <SelectItem value="none">— None —</SelectItem>
                {pms.map(u => <SelectItem key={u.id} value={u.id}>{u.name} <span className="text-neutral-500 text-[10px] ml-1">({u.role})</span></SelectItem>)}
              </SelectContent>
            </Select>
          </F>
          <F label="Priority">
            <Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}>
              <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="pf-priority"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">
                {PRIORITIES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
          <F label="Status">
            <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
              <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="pf-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">
                {PROJECT_STATUSES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
          <F label="Start Date" required error={errors.start_date}><Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} data-testid="pf-start" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          <F label="Expected Completion" required error={errors.expected_completion_date}><Input type="date" min={form.start_date || undefined} value={form.expected_completion_date} onChange={e => setForm({...form, expected_completion_date: e.target.value})} data-testid="pf-end" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          {showCosts && <>
          <F label="Currency"><CurrencySelect value={form.currency} onChange={v => setForm({...form, currency: v})} testid="pf-currency"/></F>
          <F label="Estimated Budget" required error={errors.estimated_budget}><Input type="number" min="0.01" step="any" placeholder="e.g. 150000" value={form.estimated_budget} onChange={e => setForm({...form, estimated_budget: e.target.value})} data-testid="pf-budget" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          <F label="Actual Cost" error={errors.actual_cost}><Input type="number" min="0.01" step="any" placeholder="Leave blank until known" value={form.actual_cost} onChange={e => setForm({...form, actual_cost: e.target.value})} data-testid="pf-cost" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          </>}
          <F label="Description" span={2}><Textarea rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="pf-desc" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          <F label={`Development Team (${form.development_team.length})`} span={2}>
            <div className="rounded-lg border border-[#E4E7EC] bg-[#FFFFFF] max-h-40 overflow-y-auto divide-y divide-[#E4E7EC]">
              {devs.length === 0 && <div className="p-3 text-xs text-neutral-500">No developers or testers yet. Add them under Team.</div>}
              {devs.map(u => (
                <label key={u.id} className="flex items-center gap-3 p-2.5 hover:bg-white/[0.02] cursor-pointer" data-testid={`pf-dev-${u.id}`}>
                  <Checkbox checked={form.development_team.includes(u.id)} onCheckedChange={() => toggleDev(u.id)} />
                  <div className="flex-1">
                    <div className="text-sm">{u.name}</div>
                    <div className="text-[10px] font-mono text-neutral-500">{u.email}</div>
                  </div>
                </label>
              ))}
            </div>
          </F>
          <F label="Notes" span={2}><Textarea rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} data-testid="pf-notes" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          <F label="Attachments" span={2}>
            <AttachmentPicker value={form.attachments} onChange={(a) => setForm({...form, attachments: a})} testid="pf-att" />
          </F>
        </div>
        <div className="text-[10px] font-mono text-neutral-500"><span className="text-rose-500">*</span> Mandatory fields</div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="pf-cancel">Cancel</Button>
          <Button onClick={() => save()} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="pf-save">
            {busy ? "Saving..." : (initial ? "Save Changes" : "Create Project")}
          </Button>
        </DialogFooter>
      </DialogContent>
      <HandoverDialog open={!!handover} onOpenChange={o => { if (!o) setHandover(null); }} removed={handover || []} loads={loads}
        targets={users.filter(u => form.development_team.includes(u.id) || u.id === form.project_manager_id)}
        value={handoverMap} onChange={setHandoverMap} busy={busy}
        onConfirm={() => save(Object.fromEntries((handover || []).map(r => [r.id, handoverMap[r.id] || ""])))} />
    </Dialog>
  );
}

function F({label, required, error, children, span = 1}) {
  return (
    <div className={`space-y-1.5 ${span === 2 ? "col-span-2" : ""}`}>
      <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">
        {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
      </Label>
      {children}
      {error && <div className="text-[11px] text-rose-600" data-testid="field-error">{error}</div>}
    </div>
  );
}
